import { createContext, useState, useEffect } from "react";
import getProducts from "../Data/Products";

const STORAGE_KEY = "cartItems";

const storageAvailable = () => {
  try {
    const key = "___storage_test___";
    sessionStorage.setItem(key, key);
    sessionStorage.removeItem(key);
    return true;
  } catch (err) {
    return false;
  }
};

const storage = {
  getItem: key => (storageAvailable() ? sessionStorage.getItem(key) : null),
  setItem: (key, value) => {
    if (storageAvailable()) sessionStorage.setItem(key, value);
  },
  removeItem: key => {
    if (storageAvailable()) sessionStorage.removeItem(key);
  },
};

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = storage.getItem(STORAGE_KEY);
    const parsedCart = savedCart ? JSON.parse(savedCart) : [];
    return Array.isArray(parsedCart) ? parsedCart.filter(item => item?.name) : [];
  });

  useEffect(() => {
    const savedCart = storage.getItem(STORAGE_KEY);
    const parsedCart = savedCart ? JSON.parse(savedCart) : [];
    const products = getProducts();

    const validatedCart = parsedCart
      .filter(item => {
        const current = products[item.name];
        return current && item.price;
      })
      .map(item => ({
        ...item,
        quantity: typeof item.quantity === "number" && item.quantity > 0 ? item.quantity : 1,
      }));

    if (validatedCart.length !== parsedCart.length) {
      storage.setItem(STORAGE_KEY, JSON.stringify(validatedCart));
      setCartItems(validatedCart);
    }
  }, []);

  useEffect(() => {
    storage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      storage.removeItem(STORAGE_KEY);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const addToCart = product => {
    if (!product?.name || !product?.price) {
      return;
    }

    setCartItems(prevItems => {
      const existing = prevItems.find(item => item.name === product.name);
      let updatedCart;
      if (existing) {
        updatedCart = prevItems.map(item =>
          item.name === product.name ? { ...item, quantity: (item.quantity || 1) + 1 } : item
        );
      } else {
        updatedCart = [...prevItems, { ...product, quantity: 1 }];
      }
      storage.setItem(STORAGE_KEY, JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  const updateQuantity = (productName, qty) => {
    setCartItems(prevItems => {
      let updatedCart;
      if (qty <= 0) {
        updatedCart = prevItems.filter(item => item.name !== productName);
      } else {
        updatedCart = prevItems.map(item => (item.name === productName ? { ...item, quantity: qty } : item));
      }
      storage.setItem(STORAGE_KEY, JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  const totalItemCount = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  const clearCart = () => {
    setCartItems([]);
    storage.removeItem(STORAGE_KEY);
  };

  const removeFromCart = productName => {
    setCartItems(prevItems => {
      const updatedCart = prevItems.filter(item => item?.name && item.name !== productName);
      storage.setItem(STORAGE_KEY, JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  const isInCart = productName => {
    if (!Array.isArray(cartItems)) {
      return false;
    }

    return cartItems.some(item => item?.name === productName);
  };

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, isInCart, clearCart, totalItemCount }}>
      {children}
    </CartContext.Provider>
  );
};
